// The deploy identity of a bundle: its same-origin script srcs, which Vite
// content-hashes into index.html.

// Only our own bundle files can testify about a deploy. index.html appends a
// cross-origin lottie-player tag at parse time, and there are font/analytics
// tags besides; those change on someone else's schedule and would produce
// false positives.
const fingerprintFromSrcList = (srcs) => {
  if (typeof window === 'undefined') return '';
  const { origin } = window.location;
  return (
    srcs
      .map((src) => (src || '').trim())
      .filter(Boolean)
      .filter((src) => src.startsWith('/') || src.startsWith(origin))
      .map((src) => src.replace(origin, ''))
      // Vite hashes the filename, not the query string.
      .map((src) => src.split('?')[0])
      .sort()
      .join('|')
  );
};

export const fingerprintFromHtml = (html) => {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // getAttribute, not .src: a DOMParser document has no base URL, so .src
    // on a root-relative path resolves inconsistently across browsers.
    return fingerprintFromSrcList(
      Array.from(doc.querySelectorAll('script[src]')).map((s) => s.getAttribute('src'))
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('new_version.parse_failed', e);
    return '';
  }
};

// Snapshotted at module load, before React starts lazy-loading route chunks,
// so the baseline can only ever contain the entry script index.html shipped us.
export const currentFingerprint =
  typeof document === 'undefined'
    ? ''
    : fingerprintFromSrcList(
        Array.from(document.querySelectorAll('script[src]')).map((s) => s.getAttribute('src'))
      );

let latestFingerprint = currentFingerprint;

/** Newest deploy fingerprint this tab has seen in /index.html. */
export const getLatestFingerprint = () => latestFingerprint;

export const noteLatestFingerprint = (fingerprint) => {
  if (fingerprint) latestFingerprint = fingerprint;
};

export const FINGERPRINT_CHECK_DEBOUNCE_MS = 60_000;

let checker = null;
let lastCheckRequestAt = null;

/** NewVersionBanner's index.html check. Returns an unregister function. */
export const registerFingerprintChecker = (fn) => {
  checker = fn;
  return () => {
    if (checker === fn) checker = null;
  };
};

/**
 * Runs the registered check now, at most once per debounce window. Returns
 * whether it ran.
 */
export const requestFingerprintCheck = () => {
  if (!checker) return false;
  const now = Date.now();
  if (lastCheckRequestAt !== null && now - lastCheckRequestAt < FINGERPRINT_CHECK_DEBOUNCE_MS) {
    return false;
  }
  lastCheckRequestAt = now;
  try {
    checker();
  } catch (e) {
    // the checker owns its own failures
  }
  return true;
};

export const resetFingerprintCheckForTests = () => {
  checker = null;
  lastCheckRequestAt = null;
};
